import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomDatatable from '../CustomDatatable';
import { AddIcon, NoDataFoundIcon, PencilSquareIcon } from '../../assets/Icons';
import LoadingSpinner from '../LoadingSpinner';

interface ProducerInfiniteTableProps {
  data: any[]; // Receive data as prop instead of fetching internally
  hasMore: boolean; // Receive hasMore state as prop
  isLoading: boolean; // Receive loading state as prop
  isAppending?: boolean; // Optional prop for appending state
  onLoadMore: () => void; // Callback for loading more data
  handleEditProducer?: (id: string) => void;
  handleAddWineToProducer?: (id: string) => void;
  handleProducerIdClick?: (id: string) => void;
  handleWordClick?: (word: string) => void;
  className?: string;
  fromExternalInput?: boolean;
}

const producerColumns = (
  handleEditProducer?: (id: string) => void,
  handleAddWineToProducer?: (id: string) => void,
  handleProducerIdClick?: (id: string) => void,
  handleWordClick?: (word: string) => void,
  fromExternalInput?: boolean
) => [
  {
    name: '',
    cell: (row: { id: string; name_show: string }) => (
      <button 
        className="p-1 text-blue-600 hover:text-blue-800 flex items-center gap-1" 
        onClick={() => handleEditProducer && handleEditProducer(row.id)}
      >
        <PencilSquareIcon className="w-4 h-4" />
      </button>
    ),
    sortable: false,
    maxWidth: '50px',
    style: { width: '50px', flex: '0 0 50px' }
  },
  {
    name: 'ID',
    cell: (row: { id: string; literal: string; name_show: string }) => (
      <span
        className="cursor-pointer hover:text-blue-600"
        onClick={() => handleProducerIdClick && handleProducerIdClick(row.literal)}
      >
        {row.literal}
      </span>
    ),
    maxWidth: '120px',
    style: { width: '120px', flex: '0 0 120px' }
  },
  {
    name: 'Name',
    cell: (row: { id: string; name_show: string; nameWords: string[] }) => (
      <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
        {row.nameWords?.map((word: string, idx: number) => (
          <span
            key={idx}
            className="cursor-pointer hover:text-blue-600"
            onClick={() => handleWordClick && handleWordClick(word)}
          >
            {word}{' '}
          </span>
        ))}
      </span>
    ),
    // Remove maxWidth, let it grow
    style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4', flex: '1 1 auto' }
  },
  {
    name: 'Actions',
    cell: (row: { id: string; name_show: string }) => (
      <span 
        className="px-2 py-1 bg-blue-600 text-white rounded-full cursor-pointer font-semibold flex items-center gap-1" 
        onClick={() => handleAddWineToProducer && handleAddWineToProducer(row.id)}
      >
        <AddIcon className="w-3 h-3"/>
        wine
      </span>
    ),
    sortable: false,
    maxWidth: '100px',
    style: { width: '100px', flex: '0 0 100px', textAlign: 'right' }
  }
];

const ProducerInfiniteTable: React.FC<ProducerInfiniteTableProps> = ({
  data = [],
  hasMore = false,
  isLoading = false,
  onLoadMore,
  handleEditProducer,
  handleAddWineToProducer,
  handleProducerIdClick,
  handleWordClick,
  className = '',
  fromExternalInput = false
}) => {
  const processedProducers = React.useMemo(
    () =>
      data.map(row => ({
        ...row,
        nameWords: row.name_show ? row.name_show.split(/\s+/) : [],
        literal: row.literal
      })),
    [data]
  );

  return (
    <div id="producer-infinite-table" className="flex-1 min-h-0 border border-gray-200 rounded bg-gray-50 overflow-auto flex flex-col relative">
      {isLoading && processedProducers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-full h-full w-full">
          <LoadingSpinner size="40" />
        </div>
      ) : processedProducers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-full h-full w-full">
          <NoDataFoundIcon className="w-8 h-8 text-gray-400" />
          <span className="text-gray-500 text-xs">No producers</span>
        </div>
      ) : (
        <div className="relative flex-1 h-full">
          <InfiniteScroll
            dataLength={processedProducers.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={null}
            scrollableTarget="producer-infinite-table"
            className="h-full"
          >
            <div>
              <CustomDatatable
                columns={producerColumns(handleEditProducer, handleAddWineToProducer, handleProducerIdClick, handleWordClick, fromExternalInput)}
                data={processedProducers}
                fixedHeight={false}
                className={className}
              />
            </div>
          </InfiniteScroll>
          {isLoading && processedProducers.length > 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
              <LoadingSpinner size="40" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProducerInfiniteTable;